import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, Users, Briefcase, ShieldCheck, GraduationCap, Sparkles,
  Building2, BookOpen, MessagesSquare, TrendingUp, Award, CheckCircle2
} from 'lucide-react';
import PoPShell from './PoPShell';
import { popLogin, popStats } from '../../lib/popApi';
import { usePoPAuth } from '../../context/PoPAuthContext';
import { Reveal, CountUp, AnimatedGradientBg } from '../../components/pop/motion';
import { AdvisorFAB } from '../../components/pop/engagement';
import { FAQAccordion } from '../../components/pop/mbaContent';

const POP_FAQ = [
  {
    q: 'What exactly is the SAMANVAI PoP Exchange?',
    a: 'A UGC-aligned platform that connects universities, industry experts, faculty and students on one verified network. It handles verified profiles, role posting, matchmaking, engagement workflows and impact analytics — everything that a Professor of Practice program needs, minus the friction.',
  },
  {
    q: 'How does verification work?',
    a: 'Every industry-expert profile goes through structured verification — resume + published work + patents + client outcomes + peer endorsements — reviewed manually by the SAMANVAI team. Verified profiles get a badge visible to every university on the platform.',
  },
  {
    q: 'What engagement routes do you support?',
    a: 'All three routes recognised by the UGC framework: Industry-funded (expert bears their own cost), HEI-funded (university funds the position), and Honorary (no financial exchange). Each route has its own onboarding, term structure and reporting flow.',
  },
  {
    q: 'Is there a fee to join?',
    a: 'Membership is free for individual experts, faculty and students during our founding phase. Universities pay a modest platform fee once a hire is closed. Corporate L&D partnerships and sponsored capstones follow a bespoke commercial.',
  },
  {
    q: 'How does the AI-Enabled MBA relate to the PoP Exchange?',
    a: 'The AI-Enabled MBA is our flagship program built on top of the Exchange. Verified Industry Experts on the Exchange become co-teaching faculty; universities can license the curriculum; students earn a hybrid credential co-issued with partner institutions.',
  },
  {
    q: 'Which UGC guidelines does the platform align with?',
    a: 'The 2023 UGC guidelines on Professors of Practice — 3-year renewable tenure, three engagement routes, structured verification, HEI committee approvals and impact reporting. Full framework linked from the footer.',
  },
  {
    q: 'How do I get in touch for a partnership?',
    a: 'Click the "Talk to a program advisor" button at the bottom-right of any page. You\u2019ll get a 20-minute call, tailored to your role (industry, university, faculty, corporate).',
  },
  {
    q: 'Is the platform available outside India?',
    a: 'Our current governance framework is UGC-aligned (India), but the platform itself is multi-jurisdiction ready. If your institution or expert profile is outside India, mention it during the advisor call and we\u2019ll walk through the bridge process.',
  },
];

const StakeholderTab = ({ active, onClick, label, icon: Icon, id }) => (
  <button
    onClick={onClick}
    data-testid={`stakeholder-tab-${id}`}
    className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all border ${
      active ? 'bg-white text-slate-900 border-white shadow-lg' : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
    }`}
  >
    <Icon className="w-4 h-4" />
    {label}
  </button>
);

const STAKEHOLDERS = [
  {
    id: 'industry',
    label: 'Industry Experts',
    icon: Briefcase,
    lead: 'Serving or aspiring Professors of Practice',
    role: 'industry_expert',
    bullets: [
      'Verified expert profile beyond a resume — projects, patents, client outcomes',
      'Discover HEI opportunities aligned to your domain & sector',
      'Apply, negotiate engagement route (Industry-funded / HEI-funded / Honorary)',
      'Track sessions, mentorships, and student feedback',
    ],
    ctas: [
      { to: '/pop/opportunities', label: 'Browse open PoP roles', primary: true },
      { to: '/pop/gaps',          label: 'Curriculum gaps you can close' },
      { to: '/pop/questions',     label: 'Answer practitioner questions' },
    ],
  },
  {
    id: 'university',
    label: 'Universities & HEIs',
    icon: Building2,
    lead: 'Post roles, invite experts, run UGC-compliant workflows',
    role: 'university',
    bullets: [
      'Post PoP roles with funding-route tags and term structure',
      'Nominate, screen and review candidates in one workflow',
      'Committee & Council approval gates aligned to UGC guidelines',
      'Impact analytics across departments, terms and renewals',
    ],
    ctas: [
      { to: '/pop/opportunities', label: 'Post an opportunity', primary: true },
      { to: '/pop/directory?role=industry_expert&verified_only=true', label: 'Discover verified experts' },
      { to: '/pop/dashboard',     label: 'Open committee pipeline' },
    ],
  },
  {
    id: 'faculty',
    label: 'Faculty & Academia',
    icon: BookOpen,
    lead: 'Co-design curriculum with industry Professors of Practice',
    role: 'faculty',
    bullets: [
      'Curriculum gap flagging: where PoPs can close the industry-academia divide',
      'Joint course, capstone, and research design',
      'Peer review on Professor of Practice contributions',
      'Publish case studies co-authored with industry',
    ],
    ctas: [
      { to: '/pop/gaps',      label: 'Flag a curriculum gap', primary: true },
      { to: '/pop/library',   label: 'Publish a case study' },
      { to: '/pop/directory?role=industry_expert', label: 'Find co-designers' },
    ],
  },
  {
    id: 'students',
    label: 'Students',
    icon: GraduationCap,
    lead: 'Direct access to practitioners, mentorship & industry insight',
    role: 'student',
    bullets: [
      'Enroll in courses, workshops and case studies published by verified PoPs',
      'Book 1:1 office hours & mentorship sessions',
      'Ask real-world questions on the practitioner Q&A board',
      'Track your growth on the My-Learning timeline with progress & feedback',
    ],
    ctas: [
      { to: '/pop/library',   label: 'Enroll in a course', primary: true },
      { to: '/pop/directory?role=industry_expert&verified_only=true', label: 'Find a mentor' },
      { to: '/pop/questions', label: 'Ask a practitioner' },
    ],
  },
];

const GOVERNANCE = [
  { n: '01', t: 'Nomination',         d: 'Self-nomination or institutional invitation, tagged with proposed engagement route.' },
  { n: '02', t: 'Eligibility Screen', d: 'UGC compliance checks — 15+ years, not a serving/retired academic, engagement route valid.' },
  { n: '03', t: 'Committee Review',   d: 'Domain relevance, portfolio depth, industry impact — reviewed by an institutional committee.' },
  { n: '04', t: 'Council Approval',   d: 'Academic council signs off on term, honorarium and engagement scope.' },
  { n: '05', t: 'Renewal or Exit',    d: 'Annual review; max 3-year cumulative tenure per UGC framework.' },
];

const PoPLanding = () => {
  const { user } = usePoPAuth();
  const [stakeholder, setStakeholder] = useState('industry');
  const [stats, setStats] = useState({
    total_members: 0, industry_experts: 0, universities: 0, verified_profiles: 0, open_opportunities: 0,
  });
  const active = STAKEHOLDERS.find((s) => s.id === stakeholder) || STAKEHOLDERS[0];

  useEffect(() => {
    popStats().then(setStats).catch(() => {});
  }, []);

  const startCta = user ? (
    <Link to={user.role === 'unassigned' ? '/pop/onboarding' : '/pop/dashboard'}
      data-testid="pop-continue-btn"
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-amber-300 hover:bg-amber-200 text-slate-900 text-sm font-semibold shadow-lg">
      Continue as {user.name.split(' ')[0]} <ArrowRight className="w-4 h-4" />
    </Link>
  ) : (
    <button onClick={popLogin}
      data-testid="pop-signup-hero-btn"
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-amber-300 hover:bg-amber-200 text-slate-900 text-sm font-semibold shadow-lg">
      Sign in with Google <ArrowRight className="w-4 h-4" />
    </button>
  );

  return (
    <PoPShell activeTab="home">
      {/* HERO */}
      <section className="relative grid lg:grid-cols-[1.15fr,1fr] gap-10 items-start pt-6">
        <AnimatedGradientBg className="-z-10 -inset-x-8 -top-8 rounded-2xl" />
        <div>
          <Reveal delay={0}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-300/30 bg-amber-300/5 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-amber-300" />
              <span className="text-[10px] tracking-[0.3em] font-bold text-amber-300">A CONFLUENCE OF INDUSTRY & ACADEMIA</span>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-5">
              SAMANVAI Professor of Practice <span className="text-amber-300">(PoP)</span> Exchange
              <span className="block italic text-amber-300 text-2xl sm:text-3xl lg:text-4xl mt-2">— industry &amp; academia, unified.</span>
            </h1>
          </Reveal>
          <Reveal delay={180}>
            <p className="text-base sm:text-lg text-white/70 max-w-xl mb-8">
              SAMANVAI Professor of Practice (PoP) Exchange connects universities, industry experts, faculty and students on one
              verified, UGC-aligned network — for building, verifying, discovering, engaging and growing every stakeholder.
            </p>
          </Reveal>
          <Reveal delay={260}>
            <div className="flex flex-wrap gap-3">
              {startCta}
              <Link to="/pop/directory"
                data-testid="pop-explore-directory-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-semibold text-white">
                Explore the Directory
              </Link>
              <Link to="/pop/capstone" data-testid="pop-open-capstone-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-amber-300/10 hover:bg-amber-300/20 border border-amber-300/40 text-sm font-semibold text-amber-200">
                Open Capstone Studio <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>

          {/* Compliance strip */}
          <Reveal delay={340}>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
              {[
                { l: 'UGC PoP framework', v: '3-yr max tenure' },
                { l: 'Engagement routes',  v: 'Industry · HEI · Honorary' },
                { l: 'Verification',       v: 'Manual + credential proof' },
                { l: 'Governance',         v: '5-step workflow' },
              ].map((k) => (
                <div key={k.l} className="border-t border-white/15 pt-2">
                  <div className="text-[9px] tracking-[0.25em] font-bold text-amber-300 mb-0.5">{k.l.toUpperCase()}</div>
                  <div className="text-xs text-white/85 font-medium">{k.v}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Right-side stat panel */}
        <Reveal delay={420} className="relative">
          <div className="absolute -inset-3 bg-gradient-to-br from-amber-500/10 via-emerald-500/10 to-violet-500/10 blur-2xl rounded-3xl" />
          <div className="relative rounded-2xl bg-slate-900/70 border border-white/10 backdrop-blur p-6 shadow-xl">
            <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-4">ECOSYSTEM AT A GLANCE</div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { l: 'Members',             v: stats.total_members,     Icon: Users,       animated: true },
                { l: 'Industry Experts',    v: stats.industry_experts,  Icon: Briefcase,   animated: true },
                { l: 'Universities',        v: stats.universities,      Icon: Building2,   animated: true },
                { l: 'Verified Profiles',   v: stats.verified_profiles, Icon: ShieldCheck, animated: true },
                { l: 'Open Opportunities',  v: stats.open_opportunities,Icon: TrendingUp,  animated: true },
                { l: 'Aligned with UGC',    v: '✓',                     Icon: Award,       animated: false },
              ].map(({ l, v, Icon, animated }) => (
                <div key={l} className="rounded-lg bg-white/[0.02] border border-white/10 p-4">
                  <Icon className="w-4 h-4 text-amber-300 mb-2" />
                  <div className="text-2xl font-serif font-bold text-white tabular-nums leading-none">
                    {animated ? <CountUp value={v || 0} data-testid={`stat-${l.toLowerCase().replace(/\s+/g,'-')}`} /> : v}
                  </div>
                  <div className="text-[10px] tracking-wider uppercase text-white/50 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* STAKEHOLDER MODEL */}
      <section className="mt-24">
        <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">A FOUR-STAKEHOLDER MODEL</div>
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-6">Built for everyone in the confluence.</h2>
        <div className="flex flex-wrap gap-2 mb-6">
          {STAKEHOLDERS.map((s) => (
            <StakeholderTab
              key={s.id}
              id={s.id}
              active={stakeholder === s.id}
              onClick={() => setStakeholder(s.id)}
              label={s.label}
              icon={s.icon}
            />
          ))}
        </div>
        <div className="rounded-2xl border border-white/10 bg-slate-900/50 p-6 sm:p-8">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-11 h-11 rounded-lg bg-amber-300 text-slate-900 flex items-center justify-center shadow-md">
              <active.icon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-amber-300 font-bold">{active.label}</div>
              <div className="text-lg font-serif text-white">{active.lead}</div>
            </div>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3 mb-6">
            {active.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          {/* Interactive CTAs */}
          <div className="flex flex-wrap gap-2 pt-4 border-t border-white/10">
            {(active.ctas || []).map((c) => (
              <Link key={c.to + c.label} to={c.to}
                data-testid={`stakeholder-cta-${active.id}-${c.label.slice(0, 10).replace(/\s+/g, '-').toLowerCase()}`}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-xs font-semibold transition ${
                  c.primary
                    ? 'bg-amber-300 hover:bg-amber-200 text-slate-900'
                    : 'bg-white/5 hover:bg-white/10 border border-white/10 text-white/85'
                }`}>
                {c.label} <ArrowRight className="w-3 h-3" />
              </Link>
            ))}
            {!user && (
              <button onClick={popLogin}
                data-testid={`stakeholder-enroll-${active.id}`}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-xs font-semibold bg-emerald-500 hover:bg-emerald-400 text-white">
                <Sparkles className="w-3 h-3" /> Enroll as {active.label.split(' ')[0]}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* GOVERNANCE FLOW */}
      <section className="mt-24">
        <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">GOVERNANCE WORKFLOW</div>
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-8">From nomination to renewal, in five steps.</h2>
        <div className="grid md:grid-cols-5 gap-3">
          {GOVERNANCE.map((g, i) => (
            <div key={g.n} className="relative rounded-lg border border-white/10 bg-slate-900/40 p-4">
              <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">{g.n}</div>
              <div className="text-sm font-serif font-semibold text-white mb-1">{g.t}</div>
              <div className="text-[11px] text-white/60 leading-relaxed">{g.d}</div>
              {i < 4 && (
                <div className="hidden md:block absolute top-1/2 -right-2 w-4 h-px bg-white/20" />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* KNOWLEDGE EXCHANGE + FINAL CTA */}
      <section className="mt-24 grid md:grid-cols-3 gap-5">
        {[
          { t: 'Knowledge Exchange', d: 'Co-designed courses, industry cases, joint publications, and hands-on mentorship.', Icon: BookOpen },
          { t: 'Trust & Verification', d: 'Structured proof beyond resumes — projects, outcomes, patents, endorsements.', Icon: ShieldCheck },
          { t: 'Impact & Outcomes', d: 'Sessions delivered, students mentored, curriculum contributions, renewals earned.', Icon: TrendingUp },
        ].map((c) => (
          <div key={c.t} className="rounded-xl border border-white/10 bg-slate-900/50 p-5">
            <c.Icon className="w-5 h-5 text-amber-300 mb-3" />
            <div className="text-sm font-serif font-semibold text-white mb-1">{c.t}</div>
            <div className="text-xs text-white/60 leading-relaxed">{c.d}</div>
          </div>
        ))}
      </section>

      {/* TRAINING ACADEMY — external platform */}
      <section className="mt-24" data-testid="pop-training-academy-section">
        <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">SAMANVAI TRAINING ACADEMY · EXTERNAL PLATFORM</div>
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-8">Sharpen the skills the boardroom rewards.</h2>
        <div className="relative rounded-2xl border border-white/10 bg-slate-900/50 overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-amber-300/[0.07] blur-3xl pointer-events-none" />
          <div className="grid lg:grid-cols-[1.25fr,1fr] gap-8 p-6 sm:p-8">
            <div>
              <div className="flex items-start gap-4 mb-5">
                <div className="w-11 h-11 rounded-lg bg-amber-300 text-slate-900 flex items-center justify-center shadow-md flex-shrink-0">
                  <GraduationCap className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-lg font-serif text-white">SAMANVAI Training Academy</div>
                  <div className="text-xs text-white/55 italic">Soft skills &amp; career transformation</div>
                </div>
              </div>
              <p className="text-sm text-white/70 leading-relaxed mb-6 max-w-xl">
                Expert-led, certified training on communication, leadership, emotional intelligence and
                executive presence — 300+ courses across 10 categories, engineered for professionals whose
                next role is measured in board influence, not just KPIs.
              </p>
              <div className="flex flex-wrap gap-2 mb-7">
                {['300+ expert courses', '10 skill categories', 'Certified programs', '20,000+ professionals trained'].map((p) => (
                  <span key={p} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-[11px] font-semibold text-white/70">
                    <span className="w-1 h-1 rounded-full bg-amber-300" />
                    {p}
                  </span>
                ))}
              </div>
              <a
                href="https://proposal-engine-49.preview.emergentagent.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pop-training-academy-link"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-amber-300 hover:bg-amber-200 text-slate-900 text-sm font-semibold shadow-lg group"
              >
                Enter the Academy
                <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <span className="ml-4 text-[10px] tracking-[0.2em] uppercase text-white/40 font-semibold">Opens in a new tab</span>
            </div>
            <div className="grid grid-cols-2 gap-3 content-start">
              {[
                { t: 'Communication & Public Speaking', d: 'Present and persuade with executive presence' },
                { t: 'Leadership & Teamwork', d: 'Build and inspire high-performing teams' },
                { t: 'Emotional Intelligence', d: 'Lead with awareness, empathy and impact' },
                { t: 'Negotiation & Influence', d: 'Achieve win-win outcomes in every room' },
              ].map((c) => (
                <div key={c.t} className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
                  <div className="text-[12.5px] font-serif font-semibold text-white mb-1 leading-snug">{c.t}</div>
                  <div className="text-[11px] text-white/55 leading-relaxed">{c.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mt-24" data-testid="pop-faq-section">
        <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">FREQUENTLY ASKED</div>
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-8">Everything universities and experts ask us.</h2>
        <FAQAccordion items={POP_FAQ} initialOpen={0} />
      </section>

      <section className="mt-16 rounded-2xl border border-amber-300/30 bg-amber-300/5 p-8 text-center">
        <MessagesSquare className="w-6 h-6 text-amber-300 mx-auto mb-3" />
        <h3 className="font-serif text-2xl mb-2">Ready to join the confluence?</h3>
        <p className="text-sm text-white/70 mb-5 max-w-lg mx-auto">
          One profile, five roles, endless connections — universities, experts, faculty and students on one verified network.
        </p>
        <div className="flex justify-center gap-3 flex-wrap">{startCta}</div>
      </section>

      <AdvisorFAB program="pop" defaults={{ name: user?.name || '', email: user?.email || '' }} />
    </PoPShell>
  );
};

export default PoPLanding;
